/* Olé fitting engine. Adapted from De Boleiro's parse/range recommendation base.
 * Uses Olé's published rows directly; does not estimate body circumferences.
 * No DOM, network or storage. All internal units: cm/kg.
 *
 * Sizing advice (Olé policy, restored from the original size chart):
 *  - Fan / Women: one size up from the usual size for a comfortable fit.
 *  - Player (Authentic): very slim cut, one or two sizes up from the usual size.
 *  - Kids: go by height; kits run generous, so between two sizes pick the larger.
 * `advise()` applies the table offset to a body match; `fromUsual()` applies the
 * usual-size steps. Garment (own jersey) matches are exact and get no offset.
 */
(function (root) {
  'use strict';
  const SIZE_UP = {
    hombre: { table: 1, usual: [1] },
    mujer: { table: 1, usual: [1] },
    jugador: { table: 1, usual: [1, 2] },
    nino: { table: 0, usual: [] }
  };
  function rule(kind) { return SIZE_UP[kind] || SIZE_UP.hombre; }
  function parseCell(raw) {
    if (raw == null || String(raw).trim() === '') return null;
    const text = String(raw).trim().replace(/[–−—]/g, '-').replace(/\*/g, '');
    const match = text.match(/^(\d+(?:[.,]\d+)?)(?:\s*(-|\+)\s*(\d+(?:[.,]\d+)?)?)?$/);
    if (!match || (match[2] === '-' && !match[3]) || (match[2] === '+' && match[3])) return null;
    const min = Number(match[1].replace(',', '.'));
    const max = match[2] === '+' ? Infinity : match[3] ? Number(match[3].replace(',', '.')) : min;
    return min > 0 && max >= min ? { min, max, estimated: String(raw).includes('*') } : null;
  }
  function normalizeSize(value) {
    const v = String(value).trim().toUpperCase();
    return ({ XXL:'2XL', XXXL:'3XL', XXXXL:'4XL' })[v] || v;
  }
  function distance(value, range) {
    if (!Number.isFinite(value) || !range) return null;
    return value < range.min ? range.min - value : value > range.max ? value - range.max : 0;
  }
  function entry(table, name, option, index) {
    const rows = {};
    if (index >= 0) Object.keys(table.rows).forEach(key => { rows[key] = parseCell(table.rows[key][index]); });
    return { name, available: option ? option.available === true : false, inProduct: !!option, index, rows,
      estimated: Object.values(rows).some(row => row && row.estimated), measured: index >= 0 };
  }
  function catalog(table, options) {
    return options.map(option => {
      const index = table.sizes.findIndex(size => normalizeSize(size) === normalizeSize(option.name));
      return entry(table, option.name, option, index);
    }).sort((a,b) => (a.index < 0 ? 999 : a.index) - (b.index < 0 ? 999 : b.index));
  }
  function fieldsFor(table, mode) {
    return mode === 'garment' ? ['width','length'] : table.kind === 'nino' ? ['height'] : ['height','weight'];
  }
  function recommend(table, options, profile) {
    const fields = fieldsFor(table, profile.mode);
    const limits = {height:[40,250],weight:[5,300],width:[15,150],length:[20,150]};
    const invalid = fields.filter(key => !Number.isFinite(profile[key]) || profile[key] < limits[key][0] || profile[key] > limits[key][1]);
    if (invalid.length) return { status:'invalid', fields:invalid, candidates:[], sizes:[] };
    const sizes = catalog(table,options).map(size => {
      const differences = fields.map(key => ({ key, distance:distance(profile[key],size.rows[key]) }));
      const matches = differences.every(d => d.distance === 0);
      return { ...size, differences, matches };
    });
    const matches = sizes.filter(s => s.matches);
    const candidates = matches.filter(s => s.available);
    return { status:candidates.length ? (candidates.length > 1 ? 'between' : 'match') : matches.length ? 'unavailable' : 'outside',
      fields, candidates, sizes, estimated:candidates.some(s => fields.some(key => s.rows[key].estimated)) };
  }
  /* Turns a recommend() result into sizing advice.
   * match/between → exact table size, recommended size (table offset applied), snug alternative.
   * conflict → each field's own matching sizes; recommended = the largest of them
   *            (jersey width follows weight, so the larger size wins), or null when a
   *            field is beyond every row. Never invents a size absent from the product. */
  function advise(table, result) {
    if (!result || result.status === 'invalid') return null;
    const measured = result.sizes.filter(s => s.measured);
    const matches = measured.filter(s => s.matches);
    const at = index => measured.find(s => s.index === index) || null;
    if (!matches.length) {
      const byField = {};
      result.fields.forEach(key => { byField[key] = measured.filter(s => s.differences.find(d => d.key === key).distance === 0); });
      const all = Object.values(byField).flat();
      const beyond = result.fields.filter(key => !byField[key].length);
      const recommended = !beyond.length && all.length ? all.reduce((a,b) => a.index > b.index ? a : b) : null;
      return { type:'conflict', byField, beyond, exact:null, snug:null, recommended,
        alternatives: recommended ? [{ size:recommended, role:'recommended' }] : [] };
    }
    const base = matches[matches.length - 1];
    const garment = result.fields.includes('width');
    const up = garment || matches.length > 1 ? 0 : rule(table.kind).table;
    const recommended = at(base.index + up) || base;
    const snug = matches[0];
    const alternatives = [];
    if (snug !== recommended) alternatives.push({ size:snug, role:'snug' });
    alternatives.push({ size:recommended, role:'recommended' });
    return { type: matches.length > 1 ? 'between' : 'match', exact:base, snug, recommended, alternatives,
      capped: up > 0 && recommended === base, garment };
  }
  /* Advice from the customer's usual size (adults). Steps come from SIZE_UP[kind].usual:
   * Fan/Women +1; Player +1 (recommended) and +2 (roomy). Sizes must exist in the table;
   * availability and presence in the product are reported, never substituted. */
  function fromUsual(table, options, usual) {
    const tableIndex = table.sizes.findIndex(size => normalizeSize(size) === normalizeSize(usual));
    const steps = rule(table.kind).usual;
    if (tableIndex < 0 || !steps.length) return null;
    const sizes = catalog(table, options);
    const at = index => {
      if (index < 0 || index >= table.sizes.length) return null;
      return sizes.find(s => s.index === index) || entry(table, table.sizes[index], null, index);
    };
    const usualSize = at(tableIndex);
    const ups = steps.map(step => at(tableIndex + step)).filter(Boolean);
    const recommended = ups[0] || usualSize;
    const roomy = ups[1] || null;
    const alternatives = [{ size:usualSize, role:'usual' }, { size:recommended, role:'recommended' }];
    if (roomy) alternatives.push({ size:roomy, role:'roomy' });
    return { type:'usual', exact:usualSize, snug:usualSize, recommended, roomy, alternatives, sizes,
      capped: recommended === usualSize };
  }
  function toMetric(value, field, imperial) {
    if (!Number.isFinite(value)) return NaN;
    return imperial ? value * (field === 'weight' ? 0.45359237 : 2.54) : value;
  }
  const api = {SIZE_UP,parseCell,normalizeSize,distance,catalog,fieldsFor,recommend,advise,fromUsual,toMetric};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  root.OleFitEngine = api;
})(typeof window === 'undefined' ? globalThis : window);
